import React from 'react';
import { NavBar } from '../NavBar';
import HomeLogo from '../HomeLogo';
import styled from 'styled-components';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { styled as muiStyled, useTheme } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Lottie from 'react-lottie';
import Success from '../../../assets/lotties/divider_white.json';

const GraphicContainer = styled.div`
  margin-top: -42px;
  @media screen and (min-width: 375px) {
    margin-top: -50px;
  }
  @media screen and (min-width: 425px) {
    margin-top: -56px;
  }
`;

const Banner = styled.div`
  background-color: #fff;
  ${({ color }) =>
    color && `background-color:${color === 'gray' ? '#F4F5F4' : color}`};
  ${({ colors }) =>
    !!colors?.length &&
    `background-image: linear-gradient(
    to bottom right,
    ${colors[0]},
    ${colors[1]}
  );`}
  
  height: ${({ expanded }) => (expanded === 'true' ? 150 : 60)}px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100px;
`;

const ActionButton = styled.div`
  position: absolute;
  left: 0;
`;

const LogoHeader = styled.div`
  background-color: #fff;
  ${({ color }) =>
    color && `background-color:${color === 'gray' ? '#F4F5F4' : color}`};
  ${({ isTransparent }) => isTransparent && `background-color: unset;`}
  width: 100%;
  z-index: 1097;
  display: flex;
  padding: 12px 0;
  justify-content: center;
  align-items: center;
`;

const Container = styled.div`
  padding: 0 2rem 2rem 2rem;
`;

const LeftDrawer = muiStyled(Drawer)(({ zIndex }) => ({
  gridArea: 'LeftPanel',
  width: '100%',
  zIndex: zIndex ?? 9999,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#F4F5F4',
  },
}));

const DrawerButton = styled.div`
  position: fixed;
  z-index: 1098;
  background-color: #f4f5f4;
  left: 0;
  top: 0;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DrawerCloseButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 0.3rem;
`;

const DrawerContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export function FixBannerPageLayout({
  children,
  color,
  colors,
  expanded,
  leftPanel,
  isOpen,
  hidePanelControls,
  zIndex,
  iconOnly,
  actionButton,
}) {
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: Success,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <NavBar />
      <Banner colors={colors} color={color} expanded={expanded}>
        <LogoHeader isTransparent={!!colors?.length} color={color}>
          {actionButton && <ActionButton>{actionButton}</ActionButton>}
          <HomeLogo width={iconOnly ? 40 : undefined} icon={iconOnly} />
        </LogoHeader>
      </Banner>
      {!!leftPanel && (
        <LeftDrawer anchor="left" open={isOpen || open} zIndex={zIndex}>
          <Logo>
            <HomeLogo width={iconOnly ? 40 : undefined} icon={iconOnly} />
          </Logo>
          <DrawerContainer>
            {leftPanel}
            {!hidePanelControls && (
              <DrawerCloseButton>
                <IconButton onClick={handleDrawerClose}>
                  {theme.direction === 'ltr' ? (
                    <ChevronLeftIcon />
                  ) : (
                    <ChevronRightIcon />
                  )}
                </IconButton>
              </DrawerCloseButton>
            )}
          </DrawerContainer>
        </LeftDrawer>
      )}
      {iconOnly && (
        <GraphicContainer>
          <Lottie
            style={{ transform: 'scale(-1, -1)' }}
            options={defaultOptions}
            width="100%"
            speed={0.3}
          />
        </GraphicContainer>
      )}
      <Container>{children}</Container>
      {!!leftPanel && !hidePanelControls && (
        <DrawerButton onClick={handleDrawerOpen}>
          <ChevronRightIcon />
        </DrawerButton>
      )}
    </div>
  );
}
